// Two-way texting (owner request 2026-10-03): the conversation bubbles and the composer, shared by the Texts page and
// the call screen's Text panel. Enter sends, Shift+Enter starts a new line. "Insert booking link" puts the campaign's
// Calendly setter link at the cursor (utm_source=sms). On Canadian numbers the composer RECOMMENDS ending with
// "Reply STOP to opt out." (one click adds it). The first text to a number that never texted us needs the consent tick
// ("They gave me this number and said it's OK to text it"; the server refuses without it).
// AI help (owner request, PJ, 2026-10-06): "AI draft" writes the next text from the conversation and our calls with
// them (an optional one line "Tell the AI what to say", e.g. "offer Thursday 2pm") and puts it in the message box; text
// already typed is replaced only after "Replace what you typed?". The setter edits and sends as normal: nothing is ever
// sent by the AI, and every send rule (consent tick, late hours, blocks) is unchanged. "Summarize" shows where the
// conversation stands and one next step above the box. The dialer's hotkeys never fire while either loads: the cursor
// stays in a typing box for the whole wait (sms.ts aiWaitFocus: "Tell the AI what to say" turns read-only, never
// disabled; a clicked button hands it to the message box before it goes busy), and it goes to the message box after.
import './sms.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Country } from '../../../src/shared/api';
import type { SmsAssistSummary, SmsMessage } from '../../../src/shared/api-sms';
import { SMS_ASSIST_INSTRUCTIONS_MAX, SMS_CANADA_OPT_OUT_LINE, SMS_CONSENT_LABEL, SMS_MAX_CHARS, smsSegments } from '../../../src/shared/api-sms';
import { errorMessage } from '../api';
import { useMe } from '../me';
import { calendlyLink, firstName } from './script';
import {
  aiDraftInto, aiWaitFocus, composerTakesFocus, hasOptOutLine, insertAtCursor, smsStatusLabel, smsStatusTone, smsTime,
  withOptOutLine, type SmsAssistActions,
} from './sms';

/** Oldest at the top, newest at the bottom (kept scrolled to the bottom as texts arrive). */
export function SmsBubbles(props: { messages: SmsMessage[]; empty?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const last = props.messages[props.messages.length - 1];
  useEffect(() => {
    const el = box.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [props.messages.length, last?.id, last?.status]);
  if (!props.messages.length) return <div class="vd-sms-msgs vd-sms-msgs-empty muted">{props.empty ?? 'No texts yet.'}</div>;
  return (
    <div class="vd-sms-msgs" ref={box} role="log" aria-live="polite">
      {props.messages.map((m) => {
        const status = smsStatusLabel(m);
        return (
          <div key={m.id} class={`vd-sms-msg vd-sms-${m.direction}`}>
            <div class="vd-sms-bubble">
              {m.body ? <span class="vd-sms-body">{m.body}</span> : null}
              {m.mediaCount ? <span class="vd-sms-pic">{m.mediaCount === 1 ? '(picture)' : `(${m.mediaCount} pictures)`}</span> : null}
            </div>
            <div class="vd-sms-meta">
              {smsTime(m.createdAt)}
              {m.direction === 'out' && m.userName ? ` · ${m.userName}` : ''}
              {status ? <> · <span class={`vd-sms-status vd-sms-status-${smsStatusTone(m)}`}>{status}</span></> : null}
              {m.lateConfirmed ? ' · sent after 9 PM their time' : ''}
            </div>
            {m.errorText && (m.status === 'failed' || m.status === 'undelivered') ? <div class="vd-sms-err">{m.errorText}</div> : null}
            {m.errorText && m.status === 'queued' ? <div class="vd-sms-pending">{m.errorText}</div> : null}
          </div>
        );
      })}
    </div>
  );
}

export function SmsComposer(props: {
  /** Why nothing can be sent here (do-not-call, STOP, a closed state, retired number, no number picked). null = can send. */
  blocked: string | null;
  country: Country | null;
  /** The campaign's Calendly event slug for "Insert booking link" (null: no campaign, so Your growth call). */
  calendlySlug: string | null;
  /** Whether the business is cannabis, when the screen knows (the call screen's Text panel): true gives /your-store
   *  whatever the campaign says, false never gives it; unknown uses the campaign's own link (src/shared/calendly.ts). */
  cannabis?: boolean;
  /** Offer "Insert booking link" (default true). New text shows it only once a lead with a campaign is linked. */
  bookingLink?: boolean;
  /** Sends the trimmed text (with the consent tick when it was shown); resolves true when the text was stored (sent or
   *  failed), so the box can clear. */
  onSend: (body: string, consent: boolean) => Promise<boolean>;
  placeholder?: string;
  /** Take the cursor when shown, when it becomes sendable, and when focusKey changes (never from another typing box). */
  autoFocus?: boolean;
  /** Which conversation / number the composer is for: a change clears the consent tick (and refocuses with autoFocus). */
  focusKey?: string | number | null;
  /** Show the consent tick: the first text to a number that never texted us. Send stays off until it is ticked. */
  consentNeeded?: boolean;
  /** A fact to show above the buttons, e.g. "US texts won't deliver until carriers approve our registration (pending)." */
  notice?: string | null;
  /** AI help here (sms.ts smsAssistFor): "AI draft", and "Summarize" when there are texts. null = not offered. */
  assist?: SmsAssistActions | null;
}) {
  const me = useMe();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const [aiSay, setAiSay] = useState('');                                     // "Tell the AI what to say"
  const [aiBusy, setAiBusy] = useState<'draft' | 'summary' | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiBasis, setAiBasis] = useState<string | null>(null);                // "Based on 4 texts ..." under a fresh draft
  const [summary, setSummary] = useState<SmsAssistSummary | null>(null);
  const aiSeq = useRef(0);                                                    // an answer for another conversation is dropped
  const bodyNow = useRef(body);
  bodyNow.current = body;
  const ref = useRef<HTMLTextAreaElement>(null);
  const enabled = !props.blocked;
  // The cursor goes to the message box when the panel opens (T or the Text button), when it becomes sendable after
  // loading, and when another number is picked; never away from a box the setter is typing in.
  const last = useRef<{ enabled: boolean; key: unknown } | null>(null);
  useEffect(() => {
    const prev = last.current;
    const changed = !prev || (enabled && !prev.enabled) || prev.key !== props.focusKey;
    last.current = { enabled, key: props.focusKey };
    const el = ref.current;
    const active = typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null;
    if (el && composerTakesFocus({ autoFocus: !!props.autoFocus, enabled, changed, active, activeIsComposer: active === el })) el.focus();
  }, [enabled, props.focusKey, props.autoFocus]);
  useEffect(() => {
    setConsent(false);
    aiSeq.current++;
    setAiSay(''); setAiBusy(null); setAiError(null); setAiBasis(null); setSummary(null);
  }, [props.focusKey]);
  const seg = smsSegments(body);
  const tooLong = body.length > SMS_MAX_CHARS;
  const needsTick = !!props.consentNeeded && !consent;
  const canSend = !busy && !props.blocked && !!body.trim() && !tooLong && !needsTick;

  const put = (next: string, cursor?: number) => {
    setBody(next);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      if (cursor !== undefined) el.setSelectionRange(cursor, cursor);
    });
  };
  const insertLink = () => {
    const link = calendlyLink(props.calendlySlug, firstName(me.name), 'sms', props.cannabis);
    const el = ref.current;
    const r = insertAtCursor(body, link, el?.selectionStart ?? body.length, el?.selectionEnd ?? body.length);
    put(r.body, r.cursor);
  };
  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      if (await props.onSend(body.trim(), !!props.consentNeeded && consent)) { setBody(''); setAiBasis(null); }
    } finally {
      setBusy(false);
      ref.current?.focus();
    }
  };
  /** Back to the message box; when it is disabled (blocked), off the button that was clicked (H would hang up). */
  const backToBox = () => {
    const el = ref.current;
    if (el && !el.disabled) { el.focus(); return; }
    const active = typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null;
    if (active && active.tagName === 'BUTTON') active.blur();
  };
  /** Before an AI wait: keep the cursor in a typing box (aiWaitFocus), so the buttons going busy never drop it on the
   *  page, where the setter's next keys would be dialer hotkeys. Runs before aiBusy is set. */
  const holdFocusForWait = () => {
    const el = ref.current;
    const active = typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null;
    if (el && aiWaitFocus({ active, messageBoxUsable: !el.disabled }) === 'message') el.focus();
  };
  const aiDraft = async () => {
    const assist = props.assist;
    if (!assist || aiBusy || props.blocked) return;
    const seq = ++aiSeq.current;
    holdFocusForWait();
    setAiBusy('draft'); setAiError(null); setAiBasis(null);
    try {
      const r = await assist.draft(aiSay);
      if (seq !== aiSeq.current) return;
      const next = aiDraftInto(bodyNow.current, r.draft, (q) => window.confirm(q));
      if (next === null) { backToBox(); return; }
      put(next, next.length);                 // into the message box, and the cursor with it
      setAiBasis(`AI draft. ${r.basis} Check it, edit it, then send.`);
    } catch (e) {
      if (seq !== aiSeq.current) return;
      setAiError(errorMessage(e));
      backToBox();
    } finally {
      if (seq === aiSeq.current) setAiBusy(null);
    }
  };
  const aiSummarize = async () => {
    const summarize = props.assist?.summarize;
    if (!summarize || aiBusy) return;
    const seq = ++aiSeq.current;
    holdFocusForWait();
    setAiBusy('summary'); setAiError(null);
    try {
      const r = await summarize();
      if (seq === aiSeq.current) setSummary(r);
    } catch (e) {
      if (seq === aiSeq.current) setAiError(errorMessage(e));
    } finally {
      if (seq === aiSeq.current) { setAiBusy(null); backToBox(); }
    }
  };
  const aiDraftOffered = !!props.assist && !props.blocked;

  return (
    <div class="vd-sms-composer">
      {props.blocked ? <div class="vd-sms-blocked" role="note">{props.blocked}</div> : null}
      {summary ? (
        <div class="vd-sms-summary" role="note" aria-label="Where this conversation stands">
          <div class="vd-sms-summary-head">
            <span class="eyebrow">Where it stands (AI)</span>
            <button class="btn btn-sm" type="button" aria-label="Close the summary" onClick={() => { setSummary(null); backToBox(); }}>Close</button>
          </div>
          <div class="vd-sms-summary-text">{summary.summary}</div>
          <div class="vd-sms-summary-next"><b>Next step:</b> {summary.nextStep}</div>
        </div>
      ) : null}
      <textarea ref={ref} rows={3} value={body} disabled={!!props.blocked} aria-label="Text message"
        placeholder={props.placeholder ?? 'Write a text. Enter sends; Shift+Enter starts a new line.'}
        onInput={(e) => setBody(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;
          e.preventDefault();       // the dialer's own Enter (save and dial next) never sees it
          e.stopPropagation();
          void send();
        }} />
      {props.country === 'CA' && !hasOptOutLine(body) && !props.blocked ? (
        <div class="vd-sms-hint">
          Recommended for Canada: end with '{SMS_CANADA_OPT_OUT_LINE}'
          <button class="btn btn-sm" type="button" onClick={() => put(withOptOutLine(body), withOptOutLine(body).length)}>Add it</button>
        </div>
      ) : null}
      {props.notice && !props.blocked ? <div class="vd-sms-warn" role="note">{props.notice}</div> : null}
      {props.consentNeeded && !props.blocked ? (
        <label class="vd-sms-consent">
          {/* Back to the message box after the tick: a focused checkbox would hand Enter / H to the dialer's hotkeys. */}
          <input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.currentTarget.checked); ref.current?.focus(); }} />
          <span>{SMS_CONSENT_LABEL}</span>
        </label>
      ) : null}
      {props.consentNeeded && !props.blocked ? (
        // The line our published texting page (valistix.com/sms) says we ask; carriers check that it's true.
        <div class="vd-sms-warn" role="note">Ask first: "Is it OK if I text you the booking link at this number? Message and data rates may apply, and you can reply STOP at any time."</div>
      ) : null}
      {aiDraftOffered ? (
        // Read-only, never disabled, while drafting: a disabled box would drop the cursor on the page mid-typing.
        <input class="vd-sms-ai-say" type="text" value={aiSay} maxLength={SMS_ASSIST_INSTRUCTIONS_MAX} readOnly={aiBusy === 'draft'}
          aria-label="Tell the AI what to say (optional)" placeholder={'Tell the AI what to say (optional), e.g. "offer Thursday 2pm". Enter drafts.'}
          onInput={(e) => setAiSay(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;
            e.preventDefault();       // never the dialer's Enter, and never a send: Enter here only drafts
            e.stopPropagation();
            void aiDraft();
          }} />
      ) : null}
      {aiError ? <div class="vd-sms-err" role="alert">{aiError}</div> : null}
      {aiBasis && !aiError ? <div class="vd-sms-ai-note" role="status">{aiBasis}</div> : null}
      <div class="vd-sms-actions">
        {props.bookingLink !== false ? (
          <button class="btn btn-sm" type="button" disabled={!!props.blocked} onClick={insertLink}
            title="Puts your Calendly booking link for this lead's campaign at the cursor">Insert booking link</button>
        ) : null}
        {props.assist?.summarize ? (
          <button class="vd-sms-link" type="button" disabled={!!aiBusy} onClick={() => void aiSummarize()}
            title="The AI says what they want, where it stands and one next step. Nothing is sent.">
            {aiBusy === 'summary' ? 'Summarizing…' : 'Summarize'}
          </button>
        ) : null}
        <span class={`vd-sms-count ${tooLong ? 'vd-bad' : 'muted'}`} title={`${seg.encoding}: ${seg.perSegment} characters per part`}>
          {body.length}/{SMS_MAX_CHARS} · {seg.segments} {seg.segments === 1 ? 'part' : 'parts'}
        </span>
        {aiDraftOffered ? (
          <button class="btn btn-sm" type="button" disabled={!!aiBusy || busy} onClick={() => void aiDraft()}
            title="Writes the next text from this conversation and your calls with them, into the box. You check it and send it; nothing is sent by the AI.">
            {aiBusy === 'draft' ? 'Drafting…' : 'AI draft'}
          </button>
        ) : null}
        <button class="btn btn-primary btn-sm" type="button" disabled={!canSend} title={needsTick ? 'Tick the box above first' : undefined} onClick={() => void send()}>
          {busy ? 'Sending…' : <>Send <kbd>Enter</kbd></>}
        </button>
      </div>
    </div>
  );
}
